// ============================================================================
// StageNavContextBanner
// ----------------------------------------------------------------------------
// The desktop-only ("hidden md:flex") stage-nav context pill in the top nav: a
// tone-coloured status readout (warn / success / default-orange) with an
// optional leading icon and a single-line, ellipsis-truncated message. Purely
// presentational — the tone drives the background / border / icon colour, and
// the icon (when present + registered in Icons) spins if it's the Loader2.
//
// TS-060 / DEC-COUNCIL-015 T3 (QuoteMakerNavBar sub-slice): extracted VERBATIM
// from App.jsx's top-nav render; App keeps the
// `{stageNavContext && !navMessage && …}` visibility gate; pure presentational,
// imports its own Icons.
// ============================================================================

import React from 'react';
import { Icons } from './Icon';

interface StageNavContextBannerProps {
    /** The stage-nav context readout to render (tone + optional icon + text). */
    context: {
        /** Colour band: 'warn' (amber), 'success' (green), or anything else → default orange. */
        tone?: string;
        /** Icons registry key for the leading glyph; skipped if absent or unregistered. */
        icon?: string;
        /** The single-line message (also the title tooltip). */
        text: string;
    };
}

export function StageNavContextBanner({ context }: StageNavContextBannerProps) {
    return (
        <div
            className="hidden md:flex items-center gap-2 px-3 py-1 rounded-md relative z-10"
            role="status"
            aria-live="polite"
            style={{
                background: context.tone === 'warn' ? 'rgba(251,191,36,0.12)' :
                            context.tone === 'success' ? 'rgba(16,185,129,0.12)' :
                            'color-mix(in srgb, var(--qm-accent) 12%, transparent)',
                border: `1px solid ${context.tone === 'warn' ? 'rgba(251,191,36,0.35)' :
                                      context.tone === 'success' ? 'rgba(16,185,129,0.35)' :
                                      'color-mix(in srgb, var(--qm-accent) 35%, transparent)'}`,
                color: '#fff',
                fontSize: '11px',
                maxWidth: 'min(560px, 42vw)',
                flex: '0 1 auto',
                minWidth: 0,
            }}
        >
            {context.icon && Icons[context.icon] && (
                (() => { const I = Icons[context.icon]; return <I size={12} className={context.icon === 'Loader2' ? 'animate-spin' : undefined} style={{ color: context.tone === 'warn' ? '#fbbf24' : context.tone === 'success' ? '#34d399' : 'var(--qm-accent)', flexShrink: 0 }} aria-hidden="true" />; })()
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto', minWidth: 0 }} title={context.text}>
                {context.text}
            </span>
        </div>
    );
}
